import { useEffect, useState } from 'react'
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom'
import {
  LayoutDashboard,
  BookOpen,
  BarChart2,
  CircleDollarSign,
  Users,
  Settings,
  LogOut,
  HandHeart,
  History,
  Menu,
  MessageSquare,
  X,
} from 'lucide-react'
import clsx from 'clsx'
import { useAuthStore } from '../store/authStore'

const DOCS_URL = import.meta.env.VITE_DOCS_URL as string | undefined

const navItems = [
  { to: '/dashboard', label: 'Home', icon: LayoutDashboard, end: true as const },
  { to: '/dashboard/library', label: 'Nudge Library', icon: BookOpen, adminOnly: true },
  {
    to: '/dashboard/analytics',
    label: 'Analytics',
    icon: BarChart2,
    /** Org-wide nudge/pulse/ROI — not individual performance */
    managerOrAdminOnly: true,
  },
  { to: '/dashboard/feedback-themes', label: 'Feedback themes', icon: MessageSquare, adminOnly: true },
  { to: '/dashboard/team', label: 'Team', icon: Users },
  { to: '/dashboard/accountability', label: 'Buddies', icon: HandHeart },
  { to: '/dashboard/history', label: 'History', icon: History, employeeLabel: 'My activity' as const },
  { to: '/dashboard/pricing', label: 'Pricing', icon: CircleDollarSign, adminOnly: true },
  { to: '/dashboard/settings', label: 'Settings', icon: Settings },
] as const


export function DashboardLayout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const nav = navItems
    .filter((item) => {
      if ('adminOnly' in item && item.adminOnly && user?.role !== 'admin') return false
      if (
        'managerOrAdminOnly' in item &&
        item.managerOrAdminOnly &&
        user?.role !== 'admin' &&
        user?.role !== 'manager'
      ) {
        return false
      }
      return true
    })
    .map((item) =>
      'employeeLabel' in item && item.employeeLabel && user?.role === 'employee'
        ? { ...item, label: item.employeeLabel }
        : item
    )

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [mobileOpen])

  function handleLogout() {
    logout()
    navigate('/login')
  }

  function closeMobile() {
    setMobileOpen(false)
  }

  const sidebarInner = (
    <>
      <div
        className="flex items-center justify-between gap-2 px-5 py-5"
        style={{ borderBottom: '1px solid #e0d9cc' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-2xl shrink-0">🕊</span>
          <span
            className="font-bold text-lg tracking-tight truncate"
            style={{ color: '#1a3a40', fontFamily: 'Georgia, serif' }}
          >
            Duva
          </span>
        </div>
        <button
          type="button"
          className="lg:hidden p-2 rounded-lg text-ink-soft hover:bg-warm-inset hover:text-ink -mr-2"
          onClick={closeMobile}
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1" aria-label="Workspace">
        {nav.map(({ to, label, icon: Icon, ...rest }) => (
          <NavLink
            key={to}
            to={to}
            end={'end' in rest ? rest.end : false}
            onClick={closeMobile}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={({ isActive }) =>
              isActive
                ? { background: '#EBF6FB', color: '#2A7A8A' }
                : { color: '#6b8a8f' }
            }
          >
            <Icon size={17} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-2 text-xs space-x-3" style={{ borderTop: '1px solid #e0d9cc' }}>
        <Link to="/security" className="text-[#9aafb3] hover:text-[#2A7A8A]" onClick={closeMobile}>
          Security
        </Link>
        <Link to="/privacy" className="text-[#9aafb3] hover:text-[#2A7A8A]" onClick={closeMobile}>
          Privacy
        </Link>
        {DOCS_URL ? (
          <a
            href={DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#9aafb3] hover:text-[#2A7A8A]"
            onClick={closeMobile}
          >
            Docs
          </a>
        ) : null}
      </div>

      <div className="px-3 py-4 mt-auto" style={{ borderTop: '1px solid #e0d9cc' }}>
        <div className="flex items-center gap-3 px-3 py-2 mb-1">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
            style={{ background: '#2A7A8A' }}
          >
            {user?.name?.[0]?.toUpperCase() ?? '?'}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium truncate" style={{ color: '#1a3a40' }}>
              {user?.name}
            </p>
            <p className="text-xs truncate capitalize" style={{ color: '#9aafb3' }}>
              {user?.role}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm transition-colors"
          style={{ color: '#9aafb3' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#e05a5a'
            e.currentTarget.style.background = '#fdf2f2'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#9aafb3'
            e.currentTarget.style.background = 'transparent'
          }}
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>
    </>
  )

  return (
    <div className="flex h-screen overflow-hidden flex-col lg:flex-row" style={{ background: '#F5F2EA', color: '#1a3a40' }}>
      <a
        href="#main-content"
        className="sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:m-0 focus:inline-block focus:h-auto focus:w-auto focus:overflow-visible focus:rounded-lg focus:bg-[#FDFCF8] focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:shadow-card focus:outline-none focus:ring-2 focus:ring-[#2A7A8A] focus:ring-offset-2"
      >
        Skip to main content
      </a>

      <header
        className="flex lg:hidden items-center gap-3 h-14 px-4 shrink-0 border-b z-20"
        style={{ background: '#FDFCF8', borderColor: '#e0d9cc' }}
      >
        <button
          type="button"
          className="p-2 rounded-lg text-[#6b8a8f] hover:bg-[#f0ece4] -ml-2"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
        <span className="font-bold text-base tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>
          🕊 Duva
        </span>
      </header>

      {mobileOpen && (
        <button
          type="button"
          className="lg:hidden fixed inset-0 z-40 bg-black/30"
          aria-label="Close menu"
          onClick={closeMobile}
        />
      )}

      <aside
        className={clsx(
          'flex flex-col shrink-0 w-60 max-w-[85vw] border-r z-50 transition-transform duration-200 ease-out',
          'fixed inset-y-0 left-0 lg:static lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        style={{ background: '#FDFCF8', borderColor: '#e0d9cc' }}
      >
        {sidebarInner}
      </aside>

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <main id="main-content" className="flex-1 overflow-y-auto" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
